import type { DashboardData } from "@crewledger/shared/schemas";
import { CalendarRange } from "lucide-react";
import { StatusBadge } from "@/components/design";
import { Skeleton } from "@/components/design/skeleton";

interface PayPeriodBannerProps {
  payPeriod: DashboardData["currentPayPeriod"];
  asOfDate: string | undefined;
  loading: boolean;
}

function mapStatus(
  status: string,
): "Draft" | "Submitted" | "Approved" | "Locked" | "Exported" {
  switch (status) {
    case "OPEN":
      return "Draft";
    case "IN_REVIEW":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "LOCKED":
      return "Locked";
    case "EXPORTED":
      return "Exported";
    default:
      return "Draft";
  }
}

export function PayPeriodBanner({ payPeriod, asOfDate, loading }: PayPeriodBannerProps) {
  if (loading) {
    return (
      <div className="rounded-xl border bg-navy-deep p-4 sm:p-5">
        <Skeleton className="h-4 w-32 bg-white/20" />
        <Skeleton className="mt-2 h-6 w-64 bg-white/20" />
      </div>
    );
  }

  if (!payPeriod) return null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-navy-deep/10 bg-navy-deep px-4 py-4 text-white sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-construction text-navy-deep">
          <CalendarRange className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-white/60">Current pay period</p>
          <p className="text-lg font-bold">{payPeriod.label}</p>
          <p className="text-sm text-white/70">
            {payPeriod.startDate} → {payPeriod.endDate}
            {asOfDate ? ` · Today ${asOfDate}` : null}
          </p>
        </div>
      </div>
      <StatusBadge status={mapStatus(payPeriod.status)} className="shrink-0" />
    </div>
  );
}
